import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { platformApiFetch } from './platformApi';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'invited') return 'warning';
  return 'default';
}

export default function PlatformTenantUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-users/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setUser(json.user);
    } catch (e) {
      setError(e.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !user) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!user) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{user.full_name || user.email}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.tenant_name} · <code>{user.tenant_code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/tenant-users')}>
            Back to list
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/platform/tenants/${user.tenant_id}`)}>
            Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Chip label={user.status} color={statusColor(user.status)} sx={{ mb: 2 }} />
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {row('User id', user.id)}
          {row('Tenant id', user.tenant_id)}
          {row('Email', user.email)}
          {row('Mobile', user.mobile_number)}
          {row('Roles', user.role_names || '—')}
          {row('Last login', user.last_login_at ? String(user.last_login_at) : '—')}
          {row('Invited at', user.invited_at ? String(user.invited_at) : '—')}
          {row('Activated at', user.activated_at ? String(user.activated_at) : '—')}
          {row('Created', user.created_at ? String(user.created_at) : '—')}
          {row('Updated', user.updated_at ? String(user.updated_at) : '—')}
        </Grid>
      </Paper>
    </Box>
  );
}
